import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

/**
 * Estado de abertura do `CompanyEditDrawer`.
 *
 * A gaveta é renderizada UMA vez (na Sidebar), mas é aberta de TRÊS lugares
 * diferentes — o item "Empresa" do menu Configurações, o mesmo item na sub-tela
 * de conta do drawer mobile e a busca rápida (⌘K). Antes de existir este
 * contexto o estado morava dentro da Sidebar, e por isso a busca rápida (que
 * vive fora dela) só sabia navegar por endereço: apontava para
 * `/settings/company`, uma rota que nunca existiu, e caía no NotFound.
 */
interface CompanyDrawerContextType {
  isOpen: boolean
  open: () => void
  close: () => void
}

const CompanyDrawerContext = createContext<CompanyDrawerContextType | undefined>(undefined)

interface CompanyDrawerProviderProps {
  children: ReactNode
}

export function CompanyDrawerProvider({ children }: CompanyDrawerProviderProps) {
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])

  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close])

  return (
    <CompanyDrawerContext.Provider value={value}>
      {children}
    </CompanyDrawerContext.Provider>
  )
}

export function useCompanyDrawer(): CompanyDrawerContextType {
  const context = useContext(CompanyDrawerContext)
  if (context === undefined) {
    throw new Error('useCompanyDrawer must be used within a CompanyDrawerProvider')
  }
  return context
}
